import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import { cn } from '../utils/cn';
import {
  BookTemplate,
  Search,
  Workflow,
  Zap,
  Clock,
  ArrowRight,
  Star,
  Users,
  TrendingUp,
  Bell,
  Database,
  Mail,
  Globe,
  Bot,
  Sparkles,
} from 'lucide-react';

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  nodes: number;
  uses: number;
  rating?: number;
  tags?: string[];
}

const CATEGORIES = [
  { id: 'all', name: 'All Templates', icon: BookTemplate },
  { id: 'trading', name: 'Trading', icon: TrendingUp },
  { id: 'notifications', name: 'Notifications', icon: Bell },
  { id: 'data', name: 'Data Processing', icon: Database },
  { id: 'email', name: 'Email', icon: Mail },
  { id: 'web', name: 'Web Scraping', icon: Globe },
  { id: 'ai', name: 'AI & ML', icon: Sparkles },
];

const FEATURED_TEMPLATES: Template[] = [
  {
    id: '1',
    name: 'Price Alert Bot',
    description: 'Get notified when crypto prices hit your targets. Supports multiple pairs and custom thresholds.',
    category: 'trading',
    nodes: 5,
    uses: 1234,
    rating: 4.8,
    tags: ['trading', 'alerts', 'telegram'],
  },
  {
    id: '2',
    name: 'DCA Automation',
    description: 'Automatically execute dollar-cost averaging strategy on a schedule.',
    category: 'trading',
    nodes: 4,
    uses: 892,
    rating: 4.9,
    tags: ['trading', 'automation', 'scheduled'],
  },
  {
    id: '3',
    name: 'Portfolio Tracker',
    description: 'Track your portfolio performance and generate daily/weekly reports.',
    category: 'data',
    nodes: 8,
    uses: 567,
    rating: 4.7,
    tags: ['portfolio', 'reports', 'analytics'],
  },
  {
    id: '4',
    name: 'DEX Arbitrage Scanner',
    description: 'Scan multiple DEXs for price discrepancies and execute atomic arbitrage swaps.',
    category: 'trading',
    nodes: 12,
    uses: 445,
    rating: 4.6,
    tags: ['arbitrage', 'dex', 'advanced', 'multi-chain'],
  },
  {
    id: '5',
    name: 'Sentiment Analysis',
    description: 'Analyze social media sentiment for crypto assets using AI.',
    category: 'ai',
    nodes: 6,
    uses: 334,
    rating: 4.5,
    tags: ['ai', 'sentiment', 'social'],
  },
  {
    id: '6',
    name: 'News Aggregator',
    description: 'Aggregate crypto news from multiple sources with AI summarization.',
    category: 'ai',
    nodes: 7,
    uses: 289,
    rating: 4.4,
    tags: ['news', 'ai', 'aggregation'],
  },
  {
    id: '7',
    name: 'Grid Trading Bot',
    description: 'Set up automated grid orders to profit from market volatility within a price range.',
    category: 'trading',
    nodes: 8,
    uses: 723,
    rating: 4.7,
    tags: ['grid', 'automation', 'range-trading'],
  },
  {
    id: '8',
    name: 'Cross-Chain Arbitrage',
    description: 'Monitor prices across Ethereum, BSC, Polygon, and Arbitrum for cross-chain opportunities.',
    category: 'trading',
    nodes: 15,
    uses: 298,
    rating: 4.3,
    tags: ['arbitrage', 'multi-chain', 'bridge', 'advanced'],
  },
  {
    id: '9',
    name: 'Mempool Watcher',
    description: 'Monitor pending transactions for whale movements and front-run protection.',
    category: 'trading',
    nodes: 10,
    uses: 189,
    rating: 4.2,
    tags: ['mempool', 'whale-tracking', 'mev'],
  },
  {
    id: '10',
    name: 'Uniswap V3 LP Manager',
    description: 'Automatically rebalance concentrated liquidity positions based on price action.',
    category: 'trading',
    nodes: 11,
    uses: 456,
    rating: 4.6,
    tags: ['lp', 'uniswap', 'defi', 'auto-compound'],
  },
  {
    id: '11',
    name: 'Funding Rate Arbitrage',
    description: 'Capture funding payments on perpetuals with delta-neutral positions.',
    category: 'trading',
    nodes: 9,
    uses: 267,
    rating: 4.4,
    tags: ['perps', 'funding', 'delta-neutral'],
  },
  {
    id: '12',
    name: 'Aave Yield Optimizer',
    description: 'Recursive lending strategy with auto health-factor monitoring and deleveraging.',
    category: 'trading',
    nodes: 13,
    uses: 178,
    rating: 4.5,
    tags: ['defi', 'lending', 'leverage', 'aave'],
  },
  {
    id: '13',
    name: 'Social Trading Copier',
    description: 'Copy trades from top traders on-chain with customizable position sizing.',
    category: 'trading',
    nodes: 7,
    uses: 512,
    rating: 4.3,
    tags: ['copy-trading', 'social', 'automation'],
  },
  {
    id: '14',
    name: 'Token Launch Sniper',
    description: 'Monitor new token listings and execute early buys with safety checks.',
    category: 'trading',
    nodes: 14,
    uses: 234,
    rating: 4.1,
    tags: ['sniper', 'new-tokens', 'high-risk'],
  },
  {
    id: '15',
    name: 'HBX Staking Optimizer',
    description: 'Automatically manage HBX staking with optimal lock periods and reward claiming.',
    category: 'trading',
    nodes: 6,
    uses: 678,
    rating: 4.8,
    tags: ['hbx', 'staking', 'native'],
  },
  {
    id: '16',
    name: 'AI Price Predictor',
    description: 'Use machine learning models to predict price movements and generate signals.',
    category: 'ai',
    nodes: 9,
    uses: 345,
    rating: 4.4,
    tags: ['ai', 'ml', 'prediction', 'signals'],
  },
  {
    id: '17',
    name: 'Smart Stop Loss',
    description: 'Dynamic trailing stops that execute on-chain via Gelato automation.',
    category: 'trading',
    nodes: 5,
    uses: 834,
    rating: 4.9,
    tags: ['protection', 'stop-loss', 'automation'],
  },
  {
    id: '18',
    name: 'Multi-Exchange Rebalancer',
    description: 'Keep portfolio allocations balanced across CEX and DEX positions.',
    category: 'data',
    nodes: 10,
    uses: 423,
    rating: 4.6,
    tags: ['portfolio', 'rebalance', 'multi-venue'],
  },
];

export default function Templates() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const { data: templates = FEATURED_TEMPLATES } = useQuery<Template[]>({
    queryKey: ['templates'],
    queryFn: () => api.get('/templates').then((r) => r.data).catch(() => FEATURED_TEMPLATES),
    initialData: FEATURED_TEMPLATES,
  });

  const useTemplateMutation = useMutation({
    mutationFn: (templateId: string) => api.post(`/templates/${templateId}/use`),
    onSuccess: (response) => {
      toast.success('Workflow created from template');
      navigate(`/workflow/${response.data.id}`);
    },
    onError: () => {
      toast.error('Failed to create workflow');
    },
  });

  const filteredTemplates = templates.filter((t) => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || t.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryIcon = (category: string) => {
    const found = CATEGORIES.find((c) => c.id === category);
    return found?.icon || Workflow;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary mb-1">Workflow Templates</h1>
        <p className="text-text-secondary">Start with pre-built trading bot and automation workflows</p>
      </div>

      {/* Featured Section */}
      <div className="mb-8">
        <div className="card bg-gradient-to-r from-[#161b22] via-[#1c2128] to-[#161b22] p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px]" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <Star className="h-5 w-5 text-primary" />
              <span className="text-primary font-medium">Featured</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Smart Stop Loss</h2>
            <p className="text-text-secondary mb-6 max-w-xl">
              Dynamic trailing stops that execute on-chain via Gelato automation. 
              Protect your positions 24/7 even when offline.
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2 text-text-secondary">
                <Workflow className="h-4 w-4" />
                <span>5 nodes</span>
              </div>
              <div className="flex items-center gap-2 text-text-secondary">
                <Users className="h-4 w-4" />
                <span>834 uses</span>
              </div>
              <div className="flex items-center gap-2 text-primary">
                <Star className="h-4 w-4 fill-current" />
                <span>4.9</span>
              </div>
            </div>
            <button
              onClick={() => useTemplateMutation.mutate('17')}
              className="btn-primary btn-md"
            >
              Use Template
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Search & Categories */}
      <div className="flex items-start gap-6 mb-8">
        {/* Category sidebar */}
        <div className="w-56 flex-shrink-0">
          <div className="card p-2">
            {CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all",
                    selectedCategory === category.id
                      ? "bg-primary/10 text-primary"
                      : "text-text-secondary hover:bg-white/[0.04] hover:text-text-primary"
                  )}
                >
                  <Icon className="h-4 w-4" />
                  <span>{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Templates grid */}
        <div className="flex-1">
          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#848e9c]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search templates..."
              className="input pl-11"
            />
          </div>

          {/* Grid */}
          {filteredTemplates.length === 0 ? (
            <div className="card p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-background-tertiary flex items-center justify-center mx-auto mb-4">
                <BookTemplate className="h-8 w-8 text-text-tertiary" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">No templates found</h3>
              <p className="text-text-secondary">Try adjusting your search or category filter</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              {filteredTemplates.map((template) => {
                const CategoryIcon = getCategoryIcon(template.category);
                return (
                  <div key={template.id} className="card-interactive p-5 group">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                        <CategoryIcon className="h-6 w-6 text-primary" />
                      </div>
                      {template.rating && (
                        <div className="flex items-center gap-1 text-primary">
                          <Star className="h-4 w-4 fill-current" />
                          <span className="text-sm font-medium">{template.rating}</span>
                        </div>
                      )}
                    </div>

                    <h3 className="font-semibold text-text-primary mb-2 group-hover:text-primary transition-colors">
                      {template.name}
                    </h3>
                    <p className="text-sm text-text-secondary mb-4 line-clamp-2">
                      {template.description}
                    </p>

                    <div className="flex items-center gap-4 text-xs text-text-tertiary mb-4">
                      <span className="flex items-center gap-1">
                        <Workflow className="h-3.5 w-3.5" />
                        {template.nodes} nodes
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" />
                        {template.uses.toLocaleString()} uses
                      </span>
                    </div>

                    {template.tags && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {template.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="badge-neutral">{tag}</span>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={() => useTemplateMutation.mutate(template.id)}
                      disabled={useTemplateMutation.isPending}
                      className="btn-secondary btn-sm w-full group-hover:bg-primary/10 group-hover:border-primary/30 group-hover:text-primary"
                    >
                      Use Template
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
